/**
 * ReportsShell
 *
 * The outer frame shared by every reports list page. It renders:
 *   - the left ReportsSidebar (navigation, consistent on all pages),
 *   - the standard Card scaffold (header with <h1> title, description, the
 *     always-open report search, and optional header actions, plus a
 *     CardContent that fills the available height), and
 *   - an optional inline BiblioSourceDetailsPanel on the right that appears
 *     when a report is selected in the shared reports context.
 *
 * Mirrors JournalArticlesShell without collections. Sidebar navigation is
 * driven by the URL: the shell derives the selected option from the current
 * path and navigates on change, so the sidebar does not depend on which page
 * is mounted. Search is read from and written to the reports context directly,
 * because every reports list page uses it.
 */
import { ReactNode, useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import ReportsSidebar from "@/components/reports/ReportsSidebar"
import SearchButton from "@/components/shared/SearchButton"
import BiblioSourceDetailsPanel from "@/components/shared/BiblioSourceDetailsPanel"
import {
  ReportsSidebarOption,
  pathToReportsSidebarOption,
  reportsSidebarOptionToPath,
} from "@/components/reports/reportsRoutes"
import { useReports } from "@/components/reports/ReportsContext"
import { useAuth } from "@/hooks/useAuth"
import { getParasiteRateSourceIds } from "@/services/parasiteRateService"

/**
 * Props for {@link ReportsShell}.
 */
interface ReportsShellProps {
  /** The page body (normally a ReportsTable) rendered inside the Card content. */
  children: ReactNode
  /**
   * The browser tab title and the <h1> heading for this page. Rendered via
   * React 19's automatic <title> hoisting, so both always match.
   */
  title: string
  /** A short description of what the page shows, under the heading. */
  description: string
  /** Optional actions rendered on the right of the Card header, after search. */
  headerActions?: ReactNode
  /**
   * Whether to render the inline details panel when a report is selected.
   * Table pages set this true. Defaults to false, in which case the parasite
   * rate source ids are not loaded either.
   */
  showDetailsPanel?: boolean
}

/**
 * The shared outer frame for reports list pages.
 *
 * @param props - The shell props.
 * @param props.children - The page body.
 * @param props.title - Page title for <title> and <h1>.
 * @param props.description - Short page description.
 * @param props.headerActions - Optional header actions.
 * @param props.showDetailsPanel - Whether the inline details panel can appear.
 * @returns The rendered shell element.
 */
export default function ReportsShell({
  children,
  title,
  description,
  headerActions,
  showDetailsPanel = false,
}: ReportsShellProps): React.JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()
  const { searchQuery, setSearchQuery, selectedReport, clearSelectedReport, updateReportInList } =
    useReports()

  // The selected sidebar option is derived from the URL path so the sidebar
  // highlight is correct regardless of which page component is mounted.
  const selectedOption: ReportsSidebarOption = pathToReportsSidebarOption(location.pathname)

  // The set of source IDs that have parasite rate data (latest version), used
  // by the details panel to surface a parasite-rate link. Loaded once; only
  // needed when the details panel can appear.
  const [parasiteRateSourceIds, setParasiteRateSourceIds] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!showDetailsPanel) {
      return
    }
    // Set by the cleanup so a response arriving after unmount is ignored.
    let cancelled: boolean = false

    /**
     * Load the parasite rate source ids and store them unless the effect
     * has been cleaned up in the meantime.
     *
     * @returns A promise that resolves once the ids are stored or the failure logged.
     */
    async function fetchParasiteRateSourceIds(): Promise<void> {
      try {
        const token: string = await getAccessToken()
        const { sourceIds } = await getParasiteRateSourceIds(token)
        if (!cancelled) {
          setParasiteRateSourceIds(sourceIds)
        }
      } catch (err) {
        // The parasite-rate link is a convenience, not core data, so a failure
        // here is logged rather than surfaced as a page-level error.
        console.error("Failed to fetch parasite rate source IDs:", err)
      }
    }
    void fetchParasiteRateSourceIds()
    return () => {
      cancelled = true
    }
  }, [showDetailsPanel, getAccessToken])

  /**
   * Navigate to a new sidebar option by changing the URL path. The route
   * change mounts the corresponding page component.
   *
   * @param option - The sidebar option to navigate to.
   */
  const handleSidebarOptionChange = (option: ReportsSidebarOption): void => {
    navigate(reportsSidebarOptionToPath(option), { replace: false })
  }

  return (
    <>
      <title>{title} - MAP Apps Platform</title>
      <div className="flex flex-1 min-h-0">
        <ReportsSidebar selectedOption={selectedOption} onOptionChange={handleSidebarOptionChange} />
        <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <CardTitle>
                  <h1>{title}</h1>
                </CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Prominent, always-open search inline with the title. */}
                <SearchButton
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search reports"
                  alwaysOpen
                />
                {headerActions}
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col min-h-0">{children}</CardContent>
        </Card>
        {/* Inline details panel: appears on the right when a report is
            selected, only on pages that opt in. Reports have no topics or
            collections, so that block is hidden and no collections are passed. */}
        {showDetailsPanel && selectedReport && (
          <BiblioSourceDetailsPanel
            article={selectedReport}
            parasiteRateSourceIds={parasiteRateSourceIds}
            onClose={clearSelectedReport}
            collections={[]}
            onArticleUpdate={updateReportInList}
            basePath="/reports"
            showTopicEditing={false}
            showTopicsAndCollections={false}
            entityNoun="report"
          />
        )}
      </div>
    </>
  )
}
